<template>
  <div>
    <v-skeleton-loader
      v-if="loading"
      class="m-auto"
      type="table"
      transition="scale-transition"
    />
    <TableRemote
      v-else
      :iconHeader="hasPermission('sites.procedures.edit') ? 'add' : null"
      isSearchable
      :itemActions="itemActions"
      :pagination="pagination"
      :isCustomIcon="true"
      :headers="tableHeaders"
      :items="procedures"
      hideDefaultFooter
      :isInfinitScroll="true"
      :title="$t('configuration.procedures')"
      :centerColumns="['action', 'files']"
      :notSortableColumns="['action', 'files']"
      :sortableColumns="['code', 'name', 'type']"
      @iconAction="onClickAction"
      @sendQueryParams="fetchQuery"
    >
      <template #item.files="{ item }">
        <TableFiles :files="item.files" />
      </template>
      <template #withInfiniteScroll>
        <div v-intersect="endIntersect" />
      </template>
    </TableRemote>
  </div>
</template>

<script>
import Vue from 'vue';
import { mapGetters } from 'vuex';
import { isEmpty } from 'lodash';
import TableRemote from '@/components/shared/content-views/VTableRemote';
import TableFiles from '@/components/shared/content-views/slots/TableFiles';
import roles from '@/constants/roles';
import ConfirmForm from '@/components/shared/widgets/ConfirmForm';
import AssociateProcedure from './associateProcedureForm.vue';


export default {
    components: {
        TableRemote,
        TableFiles,
    },
    data() {
        return {
            procedures: [],
            loading: false,
            queryParams: { perpage: 25 },
            pagination: {},
            initializing: false,
        };
    },
    computed: {
        ...mapGetters([
            'lang',
            'hasPermission',
            'teamMainLang',
            'teamID',
            'hasRole',
            'client',
            'site',
        ]),
        tableHeaders() {
            return [
                'code',
                'name',
                'type',
                'files',
            ].map(header => ({
                text: this.$t(`forms.labels.${header}`),
                value: header,
                sortable: header !== 'files',
            }));
        },
        associatedProceduresIds() {
            return this.procedures.map(({ procedure }) => procedure);
        },
        isAdminOrManager() {
            return this.hasRole(roles.ADMINISTRATOR.value) || this.hasRole(roles.MANAGER.value);
        },
        itemActions() {
            if (this.hasPermission('sites.procedures.remove')) {
                return [{
                    name: this.$t('forms.btn.delete'),
                    icon: 'delete',
                    mode: 'delete',
                    iconAndTextColor: '#EF4135',
                }];
            }

            return [];
        },
        hasSiteId() {
            return Boolean(this.site.id);
        },
    },
    watch: {
        async lang() {
            this.pagination = {};
            this.procedures = [];
            await this.getProcedures();
        },
    },
    async created() {
        this.loading = true;
        await this.getProcedures();
        this.loading = false;
    },
    methods: {
        async fetchQuery(queryParams) {
            this.queryParams = {
                page: 1,
                perpage: queryParams?.perpage || queryParams.perpage,
                search: queryParams?.search || '',
                order: this.preparOrderQueryParams(queryParams.order),
            };
            this.pagination = {};
            this.procedures = [];
            await this.getProcedures();
        },
        preparOrderQueryParams(value) {
            if (typeof value !== 'string') {
                return null;
            }

            const [name, order] = value.split('_');
            let newValue;
            switch (name) {
                case 'name':
                    newValue = 'name';
                    break;
                case 'code':
                    newValue = 'code';
                    break;
                case 'type':
                    newValue = 'type';
                    break;
                default:
                    return null;
            }
            return `${newValue}_${order}`;
        },

        async getProcedures() {
            if (!this.initializing && this.hasSiteId) {
                this.initializing = true;
                if (isEmpty(this.pagination) || (this.pagination?.currentPage !== this.queryParams?.page)) {
                    const { data } = await this.$rq.getSiteProcedures(this.site.id, this.queryParams);
                    this.pagination = data.pagination;
                    const resultSet = data.procedures.map(product => this.prepareProcedure(product));
                    this.procedures = [...this.procedures, ...resultSet];
                }
                this.initializing = false;
            }
        },


        prepareProcedure(procedure) {
            const { type } = procedure;

            const name = procedure.name ? procedure?.name[this.lang] || procedure?.name[this.teamMainLang] : '';
            const [selectedFile] = procedure.files ? procedure.files.filter(file => file.lang === (this.lang || this.teamMainLang)) : [];
            const files = [];
            if (selectedFile) {
                files.push({ id: selectedFile.id, fileName: selectedFile.link.fileName });
            }
            return {
                ...procedure,
                name,
                files,
                type: this.$t(`forms.labels.${type}`),
            };
        },

        async associateProcedure(requestPayload) {
            await this.$rq.associateSiteProcedure(this.site.id, requestPayload);

            this.$toasted.global.toast_success({
                message: this.$t('msg.procedureAdded', { data: requestPayload.code }),
            });
            await this.fetchQuery({});
        },

        async deleteProcedure(requestPayload) {
            await this.$rq.dissociateSiteProcedure(this.site.id, requestPayload.id);

            this.$toasted.global.toast_success({
                message: this.$t('msg.procedureRemovedSite', { data: requestPayload.code }),
            });
            await this.fetchQuery({});
        },

        onClickAdd() {
            const { associatedProceduresIds } = this;
            this.$root.$emit('showModal', {
                title: this.$t('procedures.associateProcedure'),
                showLangs: false,
                width: 750,
                component: Vue.component('ProcedureForm', AssociateProcedure),
                payload: {
                    associatedProceduresIds,
                    clientId: this.client.id,
                },
                onConfirm: requestPayload => this.associateProcedure(requestPayload),
            });
        },

        async onClickAction(procedure, action) {
            if (procedure === 'add') {
                await this.onClickAdd();
            } else if (action.mode === 'delete') {
                await this.openDeleteModal(procedure);
            }
        },

        async openDeleteModal(procedure) {
            this.$root.$emit('showModal', {
                title: this.$t('procedures.labels.remove'),
                component: Vue.component('ConfirmForm', ConfirmForm),
                payload: { msg: this.$t('procedures.labels.removeFromSite', { data: procedure.code }) },
                onConfirm: () => this.deleteProcedure(procedure),
                isForm: false,
            });
        },
        async endIntersect(_, __, isIntersecting) {
            const condition = this.pagination.currentPage < this.pagination.totalPageCount;
            if (isIntersecting && condition) {
                this.queryParams.page = this.pagination.currentPage + 1;
                await this.getProcedures();
                const ref = this.$refs?.tableRemoteRef?.$refs?.tableRemote;
                if (ref?.onScroll) {
                    ref.onScroll();
                }
            }
        },
    },
};
</script>
